import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
    AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { styled } from '@mui/material/styles';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItem from '@mui/material/ListItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&::before': {
        display: 'none',
    },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
        {...props}
    />
))(({ theme }) => ({
    backgroundColor:
        theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, .05)'
            : 'rgba(0, 0, 0, .03)',
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
        marginLeft: theme.spacing(1),
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

type ComputerInstallInstructionProps = {
    subscribed?: boolean,
    onRequestEnableNotifications?: Function,
    onRequestDisableNotifications?: Function,
}

export const ComputerInstallInstruction = (props: ComputerInstallInstructionProps): JSX.Element => {
    const {
        subscribed = false,
        onRequestEnableNotifications,
        onRequestDisableNotifications
    } = props;  


    const handleEnableNotificationChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            if (typeof onRequestEnableNotifications === "function") {
                onRequestEnableNotifications();
            }

            return;
        }

        if (typeof onRequestDisableNotifications === "function") {
            onRequestDisableNotifications();
        }
    };


    const accordionDetailsStyle: any = {
        border: 'none',
        padding: '0px 16px 16px 16px'
    }

    const accordionSummaryStyle: any = {
        background: 'none'
    }

    const imageContainerStyle: any = {
        background: '#F7F9F8',
        borderRadius: '20px',
        gridArea: 'InstallInstructionStep__ImageContainer'
    }

    const imageStyle = {
        maxWidth: '100%',
        maxHeight: 'max(20vh, 400px)'
    }

    const imageListContainerStyle = {
        display: 'flex',
        gap: '20px'
    }

    const installInstructionStepCardStyle: any = {
        m: 0,
        flexDirection: 'column',
        maxWidth: '368px',
        display: 'inline-grid',
        gridTemplateAreas: `"InstallInstructionStep__ImageContainer InstallInstructionStep__ImageContainer"
                            "InstallInstructionStep__NumberContainer InstallInstructionStep__DescriptionContainer"
                            `,
        gridTemplateColumns: 'min-content'
    }

    const installInstructionStepNumberStyle: any = {
        borderRadius: '50%',
        background: '#E7F0E6',
        width: '48px',
        height: '48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#5DA250',
        fontSize: '20px',
        fontWeight: '600'
    }

    const installInstructionStepNumberContainerStyle: any = {
        gridArea: 'InstallInstructionStep__NumberContainer',
        margin: '20px 0 0 0'
    }

    const installInstructionStepDescriptionContainerStyle: any = {
        gridArea: 'InstallInstructionStep__DescriptionContainer',
        margin: '30px 0 0 20px',
        fontSize: '16px'
    }

    const installInstructionStepListContainer: any = {
        display: 'flex',
        gap: '30px',
        flexWrap: 'wrap',
        margin: '20px 0 0 0'
    }

    const renderContent = () => {
        return (
            <Box>
                <Box>Follow the instructions to add the StrSpy icon to your Computer home screen.</Box>
                <Box sx={installInstructionStepListContainer}>
                    <Box
                        sx={installInstructionStepCardStyle}
                        className="InstallInstruction__Step InstallInstructionStep__Card"
                    >
                        <Box
                            className="InstallInstructionStep__ImageContainer"
                            sx={imageContainerStyle}>
                            <picture>
                                <source width="368" height="320" srcSet="/images/install-pwa/desktop/click-the-icon-google-chrome-0000-1920-0000-2x.png" type="image/png" />
                                <img className="main-nav-logo__image" style={imageStyle} src="/images/install-pwa/desktop/click-the-icon-google-chrome-0000-1920-0000-2x.png" alt="" loading="lazy" />
                            </picture>
                        </Box>
                        <Box
                            className="InstallInstructionStep__NumberContainer"
                            sx={installInstructionStepNumberContainerStyle}
                        >
                            <Box
                                className="InstallInstructionStep__Number"
                                sx={installInstructionStepNumberStyle}
                            >
                                1
                            </Box>
                        </Box>
                        <Box
                            className="InstallInstructionStep__DescriptionContainer"
                            sx={installInstructionStepDescriptionContainerStyle}
                        >
                            Click the icon "Google Chrome"<br/>
                            on your computer
                        </Box>
                    </Box>

                    <Box
                        sx={installInstructionStepCardStyle}
                        className="InstallInstruction__Step InstallInstructionStep__Card"
                    >
                        <Box
                            className="InstallInstructionStep__ImageContainer"
                            sx={imageContainerStyle}>
                            <picture>
                                <source width="368" height="320" srcSet="/images/install-pwa/desktop/open-app-strspy-com-and-click-0000-1920-0000-2x.png" type="image/png" />
                                <img className="main-nav-logo__image" style={imageStyle} src="/images/install-pwa/desktop/open-app-strspy-com-and-click-0000-1920-0000-2x.png" alt="" loading="lazy" />
                            </picture>
                        </Box>
                        <Box
                            className="InstallInstructionStep__NumberContainer"
                            sx={installInstructionStepNumberContainerStyle}
                        >
                            <Box
                                className="InstallInstructionStep__Number"
                                sx={installInstructionStepNumberStyle}
                            >
                                2
                            </Box>
                        </Box>
                        <Box
                            className="InstallInstructionStep__DescriptionContainer"
                            sx={installInstructionStepDescriptionContainerStyle}
                        >
                            Open <a href="https://app.strspy.com">app.StrSpy.com</a> and click icon
                            "Install app SrtSpy" on the right side
                            of the address bar
                        </Box>
                    </Box>

                    <Box
                        sx={installInstructionStepCardStyle}
                        className="InstallInstruction__Step InstallInstructionStep__Card"
                    >
                        <Box
                            className="InstallInstructionStep__ImageContainer"
                            sx={imageContainerStyle}>
                            <picture>
                                <source width="368" height="320" srcSet="/images/install-pwa/desktop/click-Install-in-the-lower-right-corner-0000-1920-0000-2x.png" type="image/png" />
                                <img className="main-nav-logo__image" style={imageStyle} src="/images/install-pwa/desktop/click-Install-in-the-lower-right-corner-0000-1920-0000-2x.png" alt="" loading="lazy" />
                            </picture>
                        </Box>
                        <Box
                            className="InstallInstructionStep__NumberContainer"
                            sx={installInstructionStepNumberContainerStyle}
                        >
                            <Box
                                className="InstallInstructionStep__Number"
                                sx={installInstructionStepNumberStyle}
                            >
                                3
                            </Box>
                        </Box>
                        <Box
                            className="InstallInstructionStep__DescriptionContainer"
                            sx={installInstructionStepDescriptionContainerStyle}
                        >
                            Click "Install" in the lower right corner.
                            Done - the icon will appear on the screen
                        </Box>
                    </Box>

                    <Box
                        sx={installInstructionStepCardStyle}
                        className="InstallInstruction__Step InstallInstructionStep__Card"
                    >
                        <Box
                            className="InstallInstructionStep__ImageContainer"
                            sx={imageContainerStyle}>
                            <picture>
                                <source width="368" height="320" srcSet="/images/install-pwa/desktop/application-icon-will-appear-on the-desktop-0000-1920-0000-2x.png" type="image/png" />
                                <img className="main-nav-logo__image" style={imageStyle} src="/images/install-pwa/desktop/application-icon-will-appear-on the-desktop-0000-1920-0000-2x.png" alt="" loading="lazy" />
                            </picture>
                        </Box>
                        <Box
                            className="InstallInstructionStep__NumberContainer"
                            sx={installInstructionStepNumberContainerStyle}
                        >
                            <Box
                                className="InstallInstructionStep__Number"
                                sx={installInstructionStepNumberStyle}
                            >
                                4
                            </Box>
                        </Box>
                        <Box
                            className="InstallInstructionStep__DescriptionContainer"
                            sx={installInstructionStepDescriptionContainerStyle}
                        >
                            Application icon will appear on the desktop
                            and will automatically launch
                        </Box>
                    </Box>
                </Box>
            </Box>
        )
    }

    return (
        <Box sx={{ minWidth: 275 }}>
            <Accordion>
                <AccordionSummary sx={accordionSummaryStyle} id="panel-header" aria-controls="panel-content">
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        How to install StrSpy on your Computer
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={accordionDetailsStyle}>
                    {renderContent()}
                </AccordionDetails>
            </Accordion>
        </Box>
    )
}